import { useState, useMemo } from "react";
import hexisS5000Colors, { 
  hexisColorsByCategory, 
  hexisCategories,
  hexisAirReleaseColors,
  hexisAirReleaseByCategory,
  hexisAirReleaseCategories,
  hexisAirReleaseFinishes,
  allHexisColors,
  allHexisColorsByCategory,
  allHexisCategories,
  hexisSeries,
  hexisHXS5000Colors
} from "@/data/hexisColors";

// Display names for series
const seriesDisplayNames = {
  "S5000": "Hexis Suptac",
  "HXS5000": "Hexis Suptac HX Air release",
  "HX20000": "Hexis Skintac Air release Wrap"
};

// Only show these series (exclude HX30000)
const visibleSeries = ["S5000", "HXS5000", "HX20000"];

export const HexisColorPalette = ({ onSelectColor, selectedColor, showAllSeries = false }) => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeSeries, setActiveSeries] = useState("S5000"); // Default to S5000 instead of "All"
  const [activeFinish, setActiveFinish] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMiniColors, setShowMiniColors] = useState(false);

  // Filter out HX30000 colors from allHexisColors
  const filteredAllColors = useMemo(() => {
    return allHexisColors.filter(c => c.series !== "HX30000");
  }, []);

  // Get Mini Colours
  const miniColors = useMemo(() => {
    return filteredAllColors.filter(c => c.miniColor === true);
  }, [filteredAllColors]);

  // Determine which colors to use based on showAllSeries prop and Mini Colours filter
  const baseColors = useMemo(() => {
    if (showMiniColors) return miniColors;
    
    if (!showAllSeries) return hexisS5000Colors;
    
    if (activeSeries === "S5000") return hexisS5000Colors.map(c => ({ ...c, series: "S5000", finish: c.name.includes("Matt") ? "Matte" : "Gloss" }));
    if (activeSeries === "HXS5000") return hexisHXS5000Colors;
    if (activeSeries === "HX20000") return hexisAirReleaseColors.filter(c => c.series === "HX20000");
    return filteredAllColors;
  }, [showAllSeries, activeSeries, filteredAllColors, showMiniColors, miniColors]);

  // Filter by category
  const categoryFilteredColors = useMemo(() => {
    if (activeCategory === "All") return baseColors;
    return baseColors.filter(c => c.category === activeCategory);
  }, [baseColors, activeCategory]);

  // Filter by finish (only for Air Release)
  const finishFilteredColors = useMemo(() => {
    if (!showAllSeries || activeFinish === "All") return categoryFilteredColors;
    return categoryFilteredColors.filter(c => c.finish === activeFinish);
  }, [categoryFilteredColors, activeFinish, showAllSeries]);

  // Filter by search term
  const searchedColors = useMemo(() => {
    if (!searchTerm) return finishFilteredColors;
    return finishFilteredColors.filter(c => 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [finishFilteredColors, searchTerm]);

  // Get unique categories from current selection
  const availableCategories = useMemo(() => {
    return [...new Set(baseColors.map(c => c.category))];
  }, [baseColors]);

  // Get category counts
  const categoryCounts = useMemo(() => {
    return availableCategories.reduce((acc, cat) => {
      acc[cat] = baseColors.filter(c => c.category === cat).length;
      return acc;
    }, {});
  }, [baseColors, availableCategories]);

  // Get available finishes from current series
  const availableFinishes = useMemo(() => {
    if (!showAllSeries) return [];
    return [...new Set(baseColors.filter(c => c.finish).map(c => c.finish))];
  }, [baseColors, showAllSeries]);

  // Series counts (only for visible series)
  const seriesCounts = useMemo(() => ({
    "S5000": hexisS5000Colors.length,
    "HXS5000": hexisHXS5000Colors.length,
    "HX20000": hexisAirReleaseColors.filter(c => c.series === "HX20000").length,
  }), []);

  // Total colors count (excluding HX30000)
  const totalColorsCount = useMemo(() => {
    return seriesCounts["S5000"] + seriesCounts["HXS5000"] + seriesCounts["HX20000"];
  }, [seriesCounts]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <h3 className="font-bold text-lg">
          {showAllSeries ? "HEXIS Vinyl Colors" : "HEXIS S5000 Vinyl Colors"}
        </h3>
        <p className="text-sm text-muted-foreground">
          {searchedColors.length} of {showAllSeries ? totalColorsCount : hexisS5000Colors.length} colors shown
        </p>
        
        {/* Search */}
        <input
          type="text"
          placeholder="Search colors by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
        />

        {/* Series Filter (only when showing all) */}
        {showAllSeries && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Series</p>
              <span className="text-xs text-green-600 font-medium">• Durability up to 10 years</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Mini Colours Tab */}
              <button
                onClick={() => {
                  setShowMiniColors(true);
                  setActiveCategory("All");
                  setActiveFinish("All");
                }}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  showMiniColors 
                    ? "bg-black text-white border-black" 
                    : "bg-white text-gray-700 border-gray-300 hover:border-black hover:text-black"
                }`}
              >
                Mini Colours ({miniColors.length})
              </button>
              {visibleSeries.map(series => (
                <button
                  key={series}
                  onClick={() => {
                    setShowMiniColors(false);
                    setActiveSeries(series);
                    setActiveCategory("All");
                    setActiveFinish("All");
                  }}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    !showMiniColors && activeSeries === series 
                      ? "bg-black text-white border-black" 
                      : "bg-white text-gray-700 border-gray-300 hover:border-black"
                  }`}
                >
                  {seriesDisplayNames[series]} ({seriesCounts[series]})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Finish Filter (only for Air Release series) */}
        {showAllSeries && availableFinishes.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Finish</p>
            <div className="flex flex-wrap gap-2">
              {availableFinishes.map(finish => (
                <button
                  key={finish}
                  onClick={() => setActiveFinish(activeFinish === finish ? "All" : finish)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    activeFinish === finish 
                      ? "bg-black text-white border-black" 
                      : "bg-white text-gray-700 border-gray-300 hover:border-black"
                  }`}
                >
                  {finish}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Category Filter */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory("All")}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                activeCategory === "All" 
                  ? "bg-black text-white border-black" 
                  : "bg-white text-gray-700 border-gray-300 hover:border-black"
              }`}
            >
              All ({baseColors.length})
            </button>
            {availableCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  activeCategory === cat 
                    ? "bg-black text-white border-black" 
                    : "bg-white text-gray-700 border-gray-300 hover:border-black"
                }`}
              >
                {cat} ({categoryCounts[cat] || 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Color Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 max-h-[500px] overflow-y-auto p-1">
        {searchedColors.map((color, idx) => {
          // Determine if color is dark for contrast adjustments
          const isDark = ['#000000', '#1A1A1A', '#0E1A40', '#0D0D0D', '#080808', '#101010', '#0F0F0F', '#191970', '#000080', '#014421', '#02402C', '#00563F', '#0A4D00', '#660000', '#800020', '#722F37', '#0B0070', '#001489', '#003399', '#002366', '#0A2463', '#4B3621', '#2E1E0F', '#5E3023', '#6B4423'].includes(color.hex) || 
                         color.hex?.toLowerCase().includes('00') && color.hex?.length === 7;
          
          return (
            <button
              key={`${color.code}-${idx}`}
              onClick={() => onSelectColor && onSelectColor(color)}
              className={`group relative aspect-square rounded-lg border transition-all hover:scale-105 overflow-hidden ${
                selectedColor?.code === color.code 
                  ? "border-black ring-2 ring-black ring-offset-2" 
                  : "border-gray-300 hover:border-gray-500 shadow-sm"
              }`}
              title={`${color.name} (${color.code})`}
            >
              {/* Base color layer */}
              <div 
                className="absolute inset-0"
                style={{ backgroundColor: color.hex }}
              />
              
              {/* Glossy overlay effect */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `linear-gradient(
                    145deg,
                    rgba(255,255,255,0.4) 0%,
                    rgba(255,255,255,0.15) 25%,
                    rgba(255,255,255,0) 50%,
                    rgba(0,0,0,0.05) 75%,
                    rgba(0,0,0,0.15) 100%
                  )`,
                }}
              />
              
              {/* Shine highlight */}
              <div 
                className="absolute top-0 left-0 right-0 h-1/3 pointer-events-none"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)',
                  borderRadius: '6px 6px 0 0',
                }}
              />
              
              {/* Inner shadow for depth */}
              <div 
                className="absolute inset-0 pointer-events-none rounded-lg"
                style={{
                  boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.1), inset 0 2px 4px rgba(255,255,255,0.1)',
                }}
              />
              
              {/* Checkmark for selected */}
              {selectedColor?.code === color.code && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <svg 
                    className={`w-6 h-6 drop-shadow-md ${isDark ? "text-white" : "text-black"}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              
              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 pointer-events-none shadow-lg">
                {color.name}
                <br />
                <span className="text-gray-400">{color.code}</span>
                {color.finish && <><br /><span className="text-gray-500">{color.finish}</span></>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Color Info */}
      {selectedColor && (
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
          <div className="w-12 h-12 rounded-lg border border-gray-300 flex-shrink-0 relative overflow-hidden shadow-sm">
            {/* Base color */}
            <div 
              className="absolute inset-0"
              style={{ backgroundColor: selectedColor.hex }}
            />
            {/* Glossy overlay */}
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(
                  145deg,
                  rgba(255,255,255,0.4) 0%,
                  rgba(255,255,255,0.15) 25%,
                  rgba(255,255,255,0) 50%,
                  rgba(0,0,0,0.05) 75%,
                  rgba(0,0,0,0.15) 100%
                )`,
              }}
            />
            {/* Shine */}
            <div 
              className="absolute top-0 left-0 right-0 h-1/3"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)',
              }}
            />
          </div>
          <div className="min-w-0">
            <p className="font-bold truncate">{selectedColor.name}</p>
            <p className="text-sm text-muted-foreground">
              Code: {selectedColor.code} | {selectedColor.category}
              {selectedColor.series && <> | {seriesDisplayNames[selectedColor.series] || selectedColor.series}</>}
              {selectedColor.finish && <> | {selectedColor.finish}</>}
            </p>
          </div>
        </div>
      )}

      {searchedColors.length === 0 && (
        <p className="text-center text-muted-foreground py-8">
          No colors found matching your filters
        </p>
      )}
    </div>
  );
};

export default HexisColorPalette;
